import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { ValidatorService } from 'app/core/services/validator.service';
import { FlorderEditMaterialComponent } from 'app/modules/florder/florder-edit-material/florder-edit-material.component';
import { TranslateService } from '@ngx-translate/core';
import { MaterialFlowPanelStatus } from 'app/modules/florder/florder-edit-container/florder-edit.interface';
import { util } from 'app/core/util/util';
import { appSettings } from 'app/appSettings';
import { Definition } from 'app/core/store/definitions/definition.interface';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { PackingMaterial } from 'app/core/store/edit-florder-detail/florder-detail.interface';

@Component({
  selector: 'app-flow-edit-material',
  templateUrl: './flow-edit-material.component.html',
  styleUrls: ['../../florder/florder-edit-material/florder-edit-material.component.scss'],
  animations: [
    trigger('growInOut', [
      state('in', style({ height: '50px' })),
      transition('void => *', [style({ height: 0 }), animate(400)]),
      transition('* => void', [animate(400, style({ height: 0 }))]),
    ]),
  ],
})
export class FlowEditMaterialComponent extends FlorderEditMaterialComponent<PackingMaterial> implements OnInit, OnChanges {
  @Input() panelStatus: MaterialFlowPanelStatus;
  @Input() maxAmountPopupIsShown: boolean;
  @Output() resetMaxAmountPopup = new EventEmitter();
  @Output() showQuantityModal = new EventEmitter();

  initialValidationMsg: string = 'Line reference must be filled';
  showLineReferenceInitialValidationMessage: boolean;
  showLineReferenceError: boolean;

  async ngOnInit() {
    await super.ngOnInit();
    this.resetForm();
  }

  async ngOnChanges(changes: SimpleChanges) {
    await super.ngOnChanges(changes);
    this.calculateShowError();
  }

  resetForm() {
    const excludedControls = ['packingId', 'internalId', 'type'];
    for (const controlName in this.editMaterialForm.controls) {
      if (!excludedControls.includes(controlName)) {
        const currentValue = this.editMaterialForm.get(controlName).value;
        if (typeof currentValue !== 'boolean') {
          // field 'isNew' must remain false or true
          this.editMaterialForm.controls[controlName].setValue('');
        }
      }
    }
    this.calculateShowError();
  }

  getContent(id: string): string {
    const brick = id && this.definitions.global.brick.find(b => b.id === id);
    return brick && brick.name;
  }

  onQuantityChange(quantity: string) {
    const treshholdQuantity = 10000;
    const q = util.toNumber(quantity);
    const isGreaterThanTreshhold = q > treshholdQuantity || q < (-1) * treshholdQuantity;
    // check if number
    if (!isNaN(q)) {
      // check if number > treshhold
      if (isGreaterThanTreshhold && !this.maxAmountPopupIsShown) {
        this.showQuantityModal.emit();
      } else if (!isGreaterThanTreshhold) {
        // Quantity is again smaller, reset show popup
        this.resetMaxAmountPopup.emit();
      }
    }
  }

  private calculateShowError() {
    const isEmptyForm = this.formIsEmpty();
    let lineReferenceIdField = this.editMaterialForm.get('lineReferenceId');
    const hasReference = this.lineItemValidation?.reference;
    const hasInvalidMessage = lineReferenceIdField.errors?.message && lineReferenceIdField.status === 'INVALID';
    const isPending = lineReferenceIdField.status === 'PENDING';
    const isValid = lineReferenceIdField.status === 'VALID';
    const isInvalid = lineReferenceIdField.status === 'INVALID';
    const hasErrors = !!this.editMaterialForm.get('lineReferenceId')?.errors;
    this.showLineReferenceInitialValidationMessage = !isEmptyForm && hasReference && !hasInvalidMessage && !isValid && !isPending;
    this.showLineReferenceError = !isEmptyForm && hasErrors && isInvalid;
  }

  protected async createForm() {
    const startingType =
      this.material && this.material.type
        ? this.material.type
        : (this.materialTypes.combination.palletIds.length && 'combination') || (this.materialTypes.packings.length && 'packing') || 'pallet';
    this.editMaterialForm = this.formBuilder.group({
      internalId: [this.material.internalId],
      type: [startingType, Validators.required],
      isNew: [this.material.isNew],
      packingId: [this.material.packingId],
      packingQuantity: [this.material.packingQuantity],
      lineReferenceId: [this.material.lineReferenceId, this.lineItemValidation && this.lineItemValidation.reference ? [Validators.required] : []],
      contentId: [this.material.contentId, this.lineItemValidation && this.lineItemValidation.content ? [Validators.required] : []],
    });

    this.listenToFormValueChanges();
    this.typeChanged();

    util.conditionalValidation(
      this.editMaterialForm,
      'packingId',
      [util.createCondition('type', [appSettings.MATERIAL_TYPE_PACKING, appSettings.MATERIAL_TYPE_COMBINATION])],
      [Validators.required]
    );
    util.conditionalValidation(this.editMaterialForm, 'packingQuantity', [util.createCondition('type', appSettings.MATERIAL_TYPE_PACKING)], [Validators.required]);

    this.editMaterialForm.valueChanges.subscribe(() => {
      this.calculateShowError();
    })
  }

  protected setPackingIds() {
    super.setPackingIds();
    if (this.panelStatus.limitedPackingIds) {
      this.packingIds = this.packingIds.filter((packing: Definition) => this.panelStatus.limitedPackingIds.indexOf(packing.id) >= 0);
    }
  }
}
